/**
 * DNS resolution enrichment collector.
 *
 * Queries Google's DNS-over-HTTPS API for domain name records.
 * Free API, no keys required — calls DNS endpoint directly.
 */

import type { CollectionTask } from '../types';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DNS_API = 'https://dns.google/resolve';
const DEFAULT_TIMEOUT_MS = 10_000;
const IP_REGEX = /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/;

const RECORD_TYPE_NAMES: Record<number, string> = {
  1: 'A',
  2: 'NS',
  5: 'CNAME',
  6: 'SOA',
  15: 'MX',
  16: 'TXT',
  28: 'AAAA',
};

/** Record types to query: A, NS, MX, TXT */
const QUERY_TYPES = [1, 2, 15, 16] as const;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DNSRecord {
  name: string;
  type: number;
  TTL: number;
  data: string;
}

export interface DNSCollectionResult {
  rawText: string;
  records: DNSRecord[];
  queryTarget: string;
}

// ---------------------------------------------------------------------------
// DNS query helper
// ---------------------------------------------------------------------------

interface GoogleDNSResponse {
  Answer?: Array<{ name: string; type: number; TTL: number; data: string }>;
}

async function queryDNS(
  domain: string,
  type: number,
  timeoutMs: number,
): Promise<DNSRecord[]> {
  const url = `${DNS_API}?name=${encodeURIComponent(domain)}&type=${type}`;

  const response = await globalThis.fetch(url, {
    headers: {
      Accept: 'application/dns-json',
      'User-Agent': 'Sentinel/1.0 OSINT Platform',
    },
    signal: AbortSignal.timeout(timeoutMs),
  });

  if (!response.ok) {
    return [];
  }

  const data = (await response.json()) as GoogleDNSResponse;
  return (data.Answer ?? []).map((r) => ({
    name: r.name,
    type: r.type,
    TTL: r.TTL,
    data: r.data,
  }));
}

// ---------------------------------------------------------------------------
// Text formatters
// ---------------------------------------------------------------------------

const TYPE_LABELS: Record<number, string> = {
  1: 'A Records (IPv4)',
  2: 'NS Records (Name Servers)',
  15: 'MX Records (Mail)',
  16: 'TXT Records',
};

function formatSection(type: number, records: DNSRecord[]): string {
  if (records.length === 0) return '';
  const label = TYPE_LABELS[type] ?? RECORD_TYPE_NAMES[type] ?? `Type ${type}`;
  const lines = records.map((r) => `  ${r.data} (TTL: ${r.TTL})`);
  return `${label}:\n${lines.join('\n')}`;
}

// ---------------------------------------------------------------------------
// Collector
// ---------------------------------------------------------------------------

export async function collectFromDNS(
  task: CollectionTask,
  timeoutMs: number = DEFAULT_TIMEOUT_MS,
): Promise<DNSCollectionResult> {
  const target = (task.query.enrichmentTarget as string)
    ?? (task.query.keywords as string[] | undefined)?.[0]
    ?? '';

  if (!target) {
    return {
      rawText: 'No target specified for DNS lookup.',
      records: [],
      queryTarget: '',
    };
  }

  if (IP_REGEX.test(target)) {
    return {
      rawText: 'DNS lookup requires a domain name, not an IP address.',
      records: [],
      queryTarget: target,
    };
  }

  // Query all record types in parallel
  const settled = await Promise.allSettled(
    QUERY_TYPES.map((type) => queryDNS(target, type, timeoutMs)),
  );

  // Extract results: fulfilled → value, rejected → empty
  const resultsByType: Record<number, DNSRecord[]> = {};
  for (let i = 0; i < QUERY_TYPES.length; i++) {
    const outcome = settled[i];
    resultsByType[QUERY_TYPES[i]] = outcome.status === 'fulfilled' ? outcome.value : [];
  }

  // Collect all records
  const allRecords: DNSRecord[] = [];
  for (const type of QUERY_TYPES) {
    allRecords.push(...resultsByType[type]);
  }

  // Build rawText
  const sections: string[] = [`DNS records for: ${target}`];
  for (const type of QUERY_TYPES) {
    const section = formatSection(type, resultsByType[type]);
    if (section) sections.push(section);
  }

  if (allRecords.length === 0) {
    sections.push('No DNS records found.');
  }

  return {
    rawText: sections.join('\n\n'),
    records: allRecords,
    queryTarget: target,
  };
}
